import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { RestaurantService } from 'src/services/data/restaurant/restaurant.service';
import { Meal } from 'src/services/classes/models/order-item';
import { Logger } from 'src/services/core/logger/logger.service';
import { CheckoutOptionsComponent } from 'src/app/shared/checkout-options/checkout-options.component';
import { OrdersService } from 'src/services/data/orders/orders.service';
import { Response } from 'src/services/classes/models/response';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { Restaurant } from 'src/services/classes/models/restaurant';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { addCanvasAsPdfPages } from 'src/services/core/pdf/add-canvas-as-pdf-pages';

const log = new Logger();

@Component({
  selector: 'app-checkout-page',
  templateUrl: './checkout-page.component.html',
  styleUrls: ['./checkout-page.component.scss'],
})
export class CheckoutPageComponent implements OnInit {
  cartTotal = 0;
  allOrders: Array<Map<string, Meal[]>> = [];
  takeawayTotal = 0;
  discounts = 0;
  tax = 0;
  subTotal = 0;
  orderCharges: { name: string; type: string; rate: number; amount: number }[] = [];
  grandTotal = 0;
  receiptDate = '';
  allTables: any[] = [];
  restaurantInfo: Restaurant | null = null;
  isGeneratingPdf = false;

  constructor(
    private restaurantService: RestaurantService,
    private router: Router,
    private bottomSheet: MatBottomSheet,
    private ordersService: OrdersService,
    private notifyService: NotificationsService
  ) {}

  ngOnInit(): void {
    // this.getCartInfo();
    this.restaurantInfo = this.restaurantService.fetchRestaurantInformation();
    if (this.restaurantInfo !== null) {
      const currentTableId = localStorage.getItem('table-number');
      const sessionId = localStorage.getItem('sessionId');
      this.getOpenTableOrders({
        restaurantBranchId: this.restaurantInfo?.branchId,
        tableId: Number(currentTableId),
        sessionId,
      });
    }
  }

  getCartInfo() {
    const raw = localStorage.getItem('allOrders');
    const listOfOrders = raw ? JSON.parse(raw) : [];
    if (listOfOrders.length > 0) {
      this.allOrders = listOfOrders.map((element: any) =>
        this.refineLisOfOrders(element)
      );
      this.getCartTotal(
        listOfOrders.map((orderItem: any) => orderItem?.order || orderItem)
      );
    }
  }

  refineLisOfOrders(orderData: any) {
    try {
      const orderDishes = orderData?.order
        ? orderData?.order?.dishes
        : orderData?.dishes;

      const cart: Map<string, Meal[]> = new Map();
      Object.entries(orderDishes).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          cart.set(
            key,
            this.refineOrderValue(value, orderData.id, orderData.status)
          );
        }
      });

      return cart;
    } catch (error) {
      log.debug('generating cart issue: ', error);
    }
  }

  refineOrderValue(
    dishContent: any[],
    orderId: number,
    orderStatus: string
  ): Meal[] | [] {
    if (dishContent.length > 0) {
      dishContent.forEach((dishItem: any) => {
        if (dishItem.addons) {
          dishItem['addOns'] = dishItem.addons;
        }

        if (dishItem.quantity) {
          dishItem['quantityOrdered'] = dishItem.quantity;
        }

        if (dishItem.unitPrice) {
          dishItem['price'] = dishItem.unitPrice;
        }

        dishItem['orderId'] = orderId;
        dishItem['orderStatus'] = orderStatus;
      });
    }

    return dishContent;
  }

  getCartTotal(orders: any[]) {
    this.cartTotal = orders.reduce(
      (acc, currentValue) => acc + parseFloat(currentValue.totalAmount),
      0
    );
  }

  handleBackNavigation() {
    const restaurantInfo = this.restaurantService.fetchRestaurantInformation();
    this.router.navigateByUrl(`/${restaurantInfo?.branchId}/dishes`);
  }

  handleCartRefresh(event: string) {
    log.debug('ev: ', event);
    if (event === 'refresh-cart') {
      setTimeout(() => this.getCartInfo(), 2000);
    }
  }

  showPaymentBottomSheet() {
    this.bottomSheet.open(CheckoutOptionsComponent, {
      data: {
        totalToPay: this.grandTotal || this.cartTotal + this.tax + this.discounts,
      },
    });
  }

  handleOrderCancellation() {
    const currentTableId = localStorage.getItem('table-number');
    if (currentTableId !== null || currentTableId !== undefined) {
      this.cancelOrder(Number(currentTableId));
    }
  }

  confirmCancelOrder(order: Map<string, Meal[]>, orderNumber: number) {
    if (window.confirm(`Are you sure you want to cancel Order ${orderNumber}?`)) {
      this.handleSingleOrderCancellation(order);
    }
  }

  handleSingleOrderCancellation(order: Map<string, Meal[]>) {
    let orderId = null;
    order.forEach((item) => (orderId = item[0].orderId));

    if (orderId !== null) {
      this.cancelSingleOrder(orderId);
    } else {
      this.notifyService.publishMessages(
        'Order cannot be cancelled',
        'warning',
        1
      );
    }
  }

  getOrderStatus(order: Map<string, Meal[]>) {
    let orderStatus = null;
    order.forEach((item) => (orderStatus = item[0].orderStatus));
    return orderStatus;
  }

  cancelSingleOrder(orderId: number) {
    const currentTableId = localStorage.getItem('table-number');

    const cancelOrder$ = this.ordersService.cancelSingleOrder(orderId);
    cancelOrder$.subscribe({
      next: (res: Response) => {
        if (res.status.toLowerCase() !== 'error') {
          res?.message
            ? this.notifyService.publishMessages(res.message, 'success', 1)
            : this.notifyService.publishMessages(
                'Order canceled',
                'success',
                1
              );
          this.getOpenTableOrders({
            restaurantBranchId: this.restaurantInfo?.branchId,
            tableId: Number(currentTableId),
            sessionId: localStorage.getItem('sessionId'),
          });
        } else {
          this.notifyService.publishMessages(res.message, 'danger', 1);
        }
      },
      error: (error) => log.debug('error: ', error),
    });
  }

  cancelOrder(tableId: number) {
    const cancelOrder$ = this.ordersService.cancelOrder(tableId);
    cancelOrder$.subscribe({
      next: (res: Response) => {
        if (res.status.toLowerCase() !== 'error') {
          res?.message
            ? this.notifyService.publishMessages(res.message, 'success', 1)
            : this.notifyService.publishMessages(
                'Order canceled',
                'success',
                1
              );
          this.getOpenTableOrders({
            restaurantBranchId: this.restaurantInfo?.branchId,
            tableId,
            sessionId: localStorage.getItem('sessionId'),
          });
        } else {
          this.notifyService.publishMessages(res.message, 'danger', 1);
        }
      },
      error: (error) => log.debug('error: ', error),
    });
  }

  getOpenTableOrders(payload: any) {
    const openTables$ = this.ordersService.getDishesForSelectedTable(payload);
    openTables$.subscribe(
      (res: Response) => {
        if (res.status.toLowerCase() !== 'error') {
          log.debug('open orders: ', res.data);
          if (
            res.data &&
            res.data.orders &&
            Array.isArray(res.data.orders) &&
            res.data.orders.length > 0
          ) {
            const orderCreationTime = res.data?.sessionStarted || null;
            const isSameDay =
              orderCreationTime !== null
                ? (moment as any)(orderCreationTime).isSame((moment as any)(), 'day')
                : false;
            const outstandingBalance = res.data?.outstandingBalance || null;
            if (
              isSameDay &&
              outstandingBalance !== null &&
              outstandingBalance > 0
            ) {
              this.subTotal = res.data?.subTotal || 0;
              this.orderCharges = res.data?.orderCharge || [];
              this.grandTotal = res.data?.outstandingBalance || 0;
              this.receiptDate = (moment as any)(res.data.sessionStarted).format('MMMM D, YYYY • h:mm A');
              localStorage.setItem(
                'allOrders',
                JSON.stringify([...res.data.orders])
              );
              setTimeout(() => this.getCartInfo(), 5);
            }
          } else {
            localStorage.removeItem('allOrders');
            localStorage.removeItem('orderData');
            setTimeout(() => this.getCartInfo(), 5);

            const restaurantInfo =
              this.restaurantService.fetchRestaurantInformation();
            setTimeout(
              () =>
                this.router.navigateByUrl(
                  `/${restaurantInfo?.branchId}/dishes`
                ),
              1000
            );
          }
        } else {
          this.notifyService.publishMessages(res.message, 'danger', 1);
        }
      },
      (error) => log.debug('error: ', error)
    );
  }

  get receiptItems(): Meal[] {
    const items: Meal[] = [];
    this.allOrders.forEach((orderMap) => {
      if (orderMap instanceof Map) {
        orderMap.forEach((meals) => items.push(...meals));
      }
    });
    return items;
  }

  get receiptAddress(): string {
    const addr = this.restaurantInfo?.address;
    if (!addr) return '';
    return [addr.line, addr.city, addr.state, addr.country]
      .filter(Boolean)
      .join(', ');
  }

  lessThanThreeHoursAgo = (sessionDate: string) => {
    const threeHours = 3 * (1000 * 60 * 60);
    // @ts-ignore
    return new Date() - new Date(sessionDate) < threeHours;
  };

  async downloadPDF() {
    const receipt = document.getElementById('receipt-content');

    if (!receipt) {
      this.notifyService.publishMessages('Unable to generate PDF', 'danger', 1);
      return;
    }

    try {
      this.isGeneratingPdf = true;

      // Bring off-screen receipt into view for capture
      receipt.style.left = '0';
      receipt.style.visibility = 'visible';

      // Two frames: first for Angular to re-render, second for layout to settle
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

      const canvas = await html2canvas(receipt, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: receipt.scrollWidth,
        windowHeight: receipt.scrollHeight,
      });

      // Push off-screen again immediately after capture
      receipt.style.left = '-9999px';
      receipt.style.visibility = 'hidden';

      const pdf = new jsPDF('p', 'mm', 'a4');
      addCanvasAsPdfPages(pdf, canvas, { receiptRoot: receipt });

      const timestamp = new Date().toISOString().slice(0, 10);
      pdf.save(`order-receipt-${timestamp}.pdf`);

      this.notifyService.publishMessages('Bill downloaded successfully!', 'success', 2);
    } catch (error) {
      log.debug('Error generating PDF:', error);
      this.notifyService.publishMessages('Failed to generate bill', 'danger', 1);
    } finally {
      this.isGeneratingPdf = false;
      if (receipt) {
        receipt.style.left = '-9999px';
        receipt.style.visibility = 'hidden';
      }
    }
  }

  async downloadImage() {
    const element = document.getElementById('pdf-content');
    const actions = document.getElementById('pdf-actions');
    
    if (!element) {
      this.notifyService.publishMessages(
        'Unable to generate image',
        'danger',
        1
      );
      return;
    }

    try {
      // Reuse loading indicator
      this.isGeneratingPdf = true;
      if (actions) actions.setAttribute('data-html2canvas-ignore', 'true');
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#eaecf0'
      });

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 10);
      link.href = dataUrl;
      link.download = `order-bill-${timestamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.notifyService.publishMessages(
        'Bill image downloaded successfully!',
        'success',
        2
      );
    } catch (error) {
      log.debug('Error generating image: ', error);
      this.notifyService.publishMessages(
        'Failed to generate image',
        'danger',
        1
      );
    } finally {
      this.isGeneratingPdf = false;
      if (actions) actions.removeAttribute('data-html2canvas-ignore');
    }
  }
}